import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilSquareIcon, PlayIcon } from "@heroicons/react/24/outline";
import { toast } from "../utils/toast";
import { bursariesApi } from "../api/hliLists";
import { errorMessageOf, fieldErrorsOf, problemOf } from "../api/scholarships";
import { Button, Card, ConfirmModal, Container, EmptyState, Input, Loading, PageHeader, RowAction, Select, Table } from "./ui";
import LoanStatusRow from "./selection/LoanStatusRow";
import { selectionColumns } from "./selection/settingsColumns";
import { BURSARIES_KEY, useStudyAreaOptions, useYearState } from "./bursaries/shared";

export const SELECTIONS_KEY = [BURSARIES_KEY, "selections"];
const INSTITUTIONS_KEY = [BURSARIES_KEY, "institutions"];

const EMPTY = { institutionId: "", studyArea: "", selectedNumber: "", tuitionFeePerStudent: "", livingAllowancePerStudent: "" };

const formOf = (s) => ({
  institutionId: s.institution.id,
  studyArea: s.studyArea,
  selectedNumber: String(s.selectedNumber),
  tuitionFeePerStudent: String(s.tuitionFeePerStudent),
  livingAllowancePerStudent: String(s.livingAllowancePerStudent),
});

// A run can change grants only while the selection is initiated or returned
const runnable = (s) => s.status === "INITIATED" || s.status === "RETURNED";

/**
 * The HEC Admin sets, per institution and study area, how many students a year's selection funds
 * and what each costs, then runs it: the best scored pending requests are granted up to the number.
 * Each institution's bursary requests are opened or closed here too.
 */
export default function SelectionComponent() {
  const queryClient = useQueryClient();
  const [year, setYear, years] = useYearState();
  const studyAreas = useStudyAreaOptions();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [running, setRunning] = useState(null);

  const institutions = useQuery({
    queryKey: [...INSTITUTIONS_KEY, year],
    enabled: Boolean(year),
    queryFn: async () => (await bursariesApi.institutions(year)).data,
  });

  const selections = useQuery({
    queryKey: [...SELECTIONS_KEY, year],
    enabled: Boolean(year),
    queryFn: async () => {
      try {
        return (await bursariesApi.selections({ academicYearId: year })).data;
      } catch (error) {
        console.error("Failed to fetch selections:", error);
        toast.error(errorMessageOf(error, "Failed to fetch the selections."), { id: "selections-error" });
        throw error;
      }
    },
  });

  const save = useMutation({
    mutationFn: () =>
      bursariesApi.setSelection({
        academicYearId: year,
        institutionId: form.institutionId,
        studyArea: form.studyArea,
        selectedNumber: Number(form.selectedNumber),
        tuitionFeePerStudent: Number(form.tuitionFeePerStudent),
        livingAllowancePerStudent: Number(form.livingAllowancePerStudent),
      }),
    onSuccess: () => {
      toast.success("Selection saved. Run it to grant the best requests.");
      setForm(EMPTY);
      setErrors({});
      queryClient.invalidateQueries({ queryKey: SELECTIONS_KEY });
    },
    onError: (error) => {
      console.error("Failed to save the selection:", error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to save the selection."));
    },
  });

  const run = useMutation({
    mutationFn: (selection) => bursariesApi.runSelection(selection.id),
    onSuccess: ({ data }) => {
      toast.success(`Selection run: ${data.grantedNow} request${data.grantedNow === 1 ? "" : "s"} granted.`);
      queryClient.invalidateQueries({ queryKey: [BURSARIES_KEY] });
    },
    onError: (error) => {
      console.error("Failed to run the selection:", error);
      toast.error(errorMessageOf(error, "Failed to run the selection."));
    },
    onSettled: () => setRunning(null),
  });

  const setWindow = useMutation({
    mutationFn: ({ institutionId, open }) => bursariesApi.setWindowFor(institutionId, open),
    onSuccess: (_, { open }) => {
      toast.success(`Bursary requests ${open ? "opened" : "closed"}.`, { autoClose: 3000 });
      queryClient.invalidateQueries({ queryKey: INSTITUTIONS_KEY });
    },
    onError: (error) => {
      console.error("Failed to change the request window:", error);
      toast.error(errorMessageOf(error, "Failed to change the request window."));
    },
  });

  const field = (key) => ({ value: form[key], error: errors[key], onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })) });
  const complete = Object.values(form).every((v) => v !== "");
  const institutionOptions = (institutions.data ?? []).map((r) => ({ value: r.institution.id, label: r.institution.name }));
  const togglingId = setWindow.isPending ? setWindow.variables?.institutionId : null;

  const columns = [
    ...selectionColumns(true),
    {
      key: "actions",
      label: <span className="sr-only">Actions</span>,
      exportable: false,
      render: (s) =>
        s.status !== "APPROVED" && (
          <div className="flex justify-end gap-1 whitespace-nowrap">
            <RowAction label="Edit" variant="ghost" Icon={PencilSquareIcon} onClick={() => setForm(formOf(s))} />
            {runnable(s) && <RowAction label="Run" variant="soft" Icon={PlayIcon} loading={run.isPending && running?.id === s.id} onClick={() => setRunning(s)} />}
          </div>
        ),
    },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Selection & Loan Management"
        subtitle="Set and run each institution's selection, and manage when institutions accept bursary requests"
        actions={<Select aria-label="Academic year" value={year} onChange={(e) => setYear(e.target.value)} options={years} className="w-44" />}
      />

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
        <Card title="Selection" subtitle="How many students of an institution's study area are funded, and what each costs" bodyClassName="flex flex-col gap-4">
          <Select label="Institution" required placeholder="Select institution" options={institutionOptions} {...field("institutionId")} />
          <Select label="Study area" required placeholder="Select study area" options={studyAreas} {...field("studyArea")} />
          <Input label="Students funded" required type="number" min={1} {...field("selectedNumber")} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Tuition fee per student (RWF)" required type="number" min={0} {...field("tuitionFeePerStudent")} />
            <Input label="Living allowance per student (RWF)" required type="number" min={0} {...field("livingAllowancePerStudent")} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setForm(EMPTY)} disabled={save.isPending}>
              Clear
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!year || !complete}>
              Save selection
            </Button>
          </div>
        </Card>

        <Card title="Bursary Request Settings" subtitle="Open or close bursary requests for each institution" bodyClassName="p-0">
          {institutions.isPending ? (
            <Loading label="Loading institutions…" />
          ) : institutions.isError ? (
            <EmptyState title="Failed to load the institutions" onRefresh={institutions.refetch} />
          ) : institutions.data.length === 0 ? (
            <EmptyState title="No institutions found" description="No public institution is configured yet." />
          ) : (
            <ul className="thin-scrollbar max-h-[28rem] divide-y divide-slate-100 overflow-y-auto">
              {institutions.data.map((r) => (
                <LoanStatusRow
                  key={r.institution.id}
                  name={r.institution.name}
                  open={r.requestsOpen}
                  loading={togglingId === r.institution.id}
                  onChange={(open) => setWindow.mutate({ institutionId: r.institution.id, open })}
                />
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Table cardView
        columns={columns}
        data={selections.data ?? []}
        isLoading={selections.isPending && Boolean(year)}
        searchPlaceholder="Search by institution or study area"
        exportName="selections"
        emptyTitle="No selection set for this year"
      />

      <ConfirmModal
        open={Boolean(running)}
        onClose={() => setRunning(null)}
        onConfirm={() => run.mutate(running)}
        loading={run.isPending}
        title={running ? `Run the ${running.studyArea} selection of ${running.institution.name}?` : ""}
        description={running ? `The best scored pending requests are granted, up to ${running.selectedNumber} students in all. Students see the grants once the selection is approved.` : ""}
        confirmLabel="Run selection"
      />
    </Container>
  );
}
